import type { GatewayFilters, GatewayLogItem } from "./types";

const API_BASE = "/api/ai-gateway";
const ADMIN_BASE = `${API_BASE}/manage`;
const INVOKE_BASE = `${API_BASE}/invoke`;
const DOCS_BASE = `${API_BASE}/docs`;
let localMasterKey = "";

export class GatewayAPIError extends Error {
  status: number;
  authMode: string;

  constructor(status: number, message: string, authMode = "") {
    super(message);
    this.name = "GatewayAPIError";
    this.status = status;
    this.authMode = authMode;
  }
}

export function setLocalGatewayMasterKey(value: string) {
  localMasterKey = value.trim();
}

function requestHeaders(init?: HeadersInit) {
  const headers = new Headers(init);
  if (localMasterKey) {
    headers.set("Authorization", `Bearer ${localMasterKey}`);
  }
  return headers;
}

async function gatewayError(response: Response, fallback: string) {
  const text = await response.text().catch(() => "");
  let payload: { error?: string } = {};
  try {
    payload = text ? (JSON.parse(text) as { error?: string }) : {};
  } catch {
    payload = {};
  }
  return new GatewayAPIError(
    response.status,
    payload.error || text || fallback,
    response.headers.get("X-Console-Auth-Mode") || "",
  );
}

async function jsonRequest<T>(base: string, path: string, init: RequestInit = {}) {
  const headers = requestHeaders(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const response = await fetch(`${base}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    throw await gatewayError(response, `Gateway request failed (${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function gatewayAPI<T>(path: string, init: RequestInit = {}) {
  return jsonRequest<T>(API_BASE, path, init);
}

export async function gatewayAdminAPI<T>(path: string, init: RequestInit = {}) {
  return jsonRequest<T>(ADMIN_BASE, path, init);
}

export async function gatewayInvoke(path: string, init: RequestInit = {}) {
  const response = await fetch(`${INVOKE_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: requestHeaders(init.headers),
  });
  if (!response.ok) {
    throw await gatewayError(response, `Gateway invocation failed (${response.status})`);
  }
  return response;
}

export async function gatewayDocs<T>(path: string) {
  return jsonRequest<T>(DOCS_BASE, path);
}

export async function downloadGatewayExport(path: string, filename: string) {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: requestHeaders(),
  });
  if (!response.ok) {
    throw await gatewayError(response, `Gateway export failed (${response.status})`);
  }
  const objectURL = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = objectURL;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectURL);
}

export function gatewayQuery(
  filters: GatewayFilters,
  extra: Record<string, string> = {},
) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries({ ...filters, ...extra })) {
    if (value) {
      params.set(key === "search" ? "q" : key, value);
    }
  }
  return params.toString();
}

function parseSSEBlock(block: string) {
  const lines = block.split("\n");
  return {
    id: lines.find((line) => line.startsWith("id: "))?.slice(4) || "",
    data: lines.find((line) => line.startsWith("data: "))?.slice(6) || "",
  };
}

export async function streamGatewayLogs(
  filters: GatewayFilters,
  cursor: string,
  onLog: (item: GatewayLogItem) => void,
  signal: AbortSignal,
) {
  let lastCursor = cursor;
  let retryDelay = 250;

  while (!signal.aborted) {
    try {
      const response = await fetch(
        `${API_BASE}/logs/events?${gatewayQuery(filters, lastCursor ? { cursor: lastCursor } : {})}`,
        {
          credentials: "include",
          headers: requestHeaders({ Accept: "text/event-stream" }),
          signal,
        },
      );
      if (!response.ok || !response.body) {
        throw await gatewayError(response, "Could not open the live request stream");
      }

      retryDelay = 250;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (!signal.aborted) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buffer += decoder.decode(chunk.value, { stream: true }).replace(/\r\n/g, "\n");
        let boundary = buffer.indexOf("\n\n");
        while (boundary >= 0) {
          const event = parseSSEBlock(buffer.slice(0, boundary));
          buffer = buffer.slice(boundary + 2);
          if (event.id) lastCursor = event.id;
          if (event.data) onLog(JSON.parse(event.data) as GatewayLogItem);
          boundary = buffer.indexOf("\n\n");
        }
      }
    } catch (error) {
      if (signal.aborted) return;
      if (
        error instanceof GatewayAPIError &&
        (error.status === 401 || error.status === 403)
      ) {
        throw error;
      }
    }

    if (signal.aborted) return;
    await new Promise<void>((resolve) => {
      const timer = globalThis.setTimeout(resolve, retryDelay);
      signal.addEventListener(
        "abort",
        () => {
          globalThis.clearTimeout(timer);
          resolve();
        },
        { once: true },
      );
    });
    retryDelay = Math.min(retryDelay * 2, 10_000);
  }
}
